import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [verificationStillRequired, setVerificationStillRequired] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setSuccessMessage(data?.message || "Password updated.");
      setVerificationStillRequired(Boolean(data?.email_verification_required));
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card max-w-xl space-y-6" data-testid="reset-password-page">
      <h1 className="text-base md:text-lg font-semibold">Choose a new password</h1>
      {done ? (
        <div className="space-y-4">
          <p data-testid="reset-success-message">{successMessage}</p>
          {verificationStillRequired && (
            <div className="space-y-2 text-sm text-muted-foreground" data-testid="reset-verification-reminder">
              <p>
                <strong>Email verification is still required.</strong> Resetting your password does not verify your
                email address.
              </p>
              <p>
                On the login page, enter this email and use <strong>Resend verification</strong> after your password is
                accepted, then open the link from your inbox before logging in.
              </p>
            </div>
          )}
          <Link to="/login" className="btn-primary inline-block" data-testid="reset-login-link">
            {verificationStillRequired ? "Go to login to verify email" : "Log in"}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {!token && <p role="alert" className="text-destructive" data-testid="reset-no-token">No reset token provided.</p>}
          <div className="space-y-1">
            <label htmlFor="reset-password" className="font-medium">New password (minimum 8 characters)</label>
            <input
              id="reset-password"
              data-testid="reset-password-input"
              className="input"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && <p role="alert" className="text-destructive" data-testid="reset-error">{error}</p>}
          <button type="submit" className="btn-primary" disabled={busy || !token} data-testid="reset-submit-button">
            {busy ? "Saving…" : "Save new password"}
          </button>
        </form>
      )}
    </div>
  );
}
